import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  getEmbedParams,
  initBridge,
  type ShellContext,
  type BridgeAPI,
  type EmbedParams,
} from '@/lib/embedBridge';
import { setAuthToken } from '@/lib/api';

interface EmbedContextValue {
  isEmbedded: boolean;
  isReady: boolean;
  shellContext: ShellContext | null;
  bridge: BridgeAPI | null;
  embedParams: EmbedParams;
  theme: 'light' | 'dark';
  /** Register a handler for shell-initiated navigation (used by useRouteSync) */
  registerNavigateHandler: (handler: (path: string) => void) => void;
}

const EmbedContext = createContext<EmbedContextValue | null>(null);

interface EmbedProviderProps {
  children: ReactNode;
}

export function EmbedProvider({ children }: EmbedProviderProps) {
  const [embedParams] = useState<EmbedParams>(() => getEmbedParams());
  const [isReady, setIsReady] = useState(false);
  const [shellContext, setShellContext] = useState<ShellContext | null>(null);
  const [bridge, setBridge] = useState<BridgeAPI | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  // Navigate handler ref for shell-initiated navigation (registered by useRouteSync)
  const navigateHandlerRef = useRef<((path: string) => void) | null>(null);

  const registerNavigateHandler = useCallback((handler: (path: string) => void) => {
    navigateHandlerRef.current = handler;
  }, []);

  const handleContextUpdate = useCallback((context: ShellContext) => {
    setShellContext(context);
    setIsReady(true);
  }, []);

  const handleThemeChange = useCallback((newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  }, []);

  useEffect(() => {
    // Set auth token from URL params
    if (embedParams.token) {
      setAuthToken(embedParams.token);
    }

    // If not embedded, mark as ready immediately
    if (!embedParams.isEmbedded) {
      setIsReady(true);
      return;
    }

    // Initialize bridge if embedded
    if (embedParams.shellOrigin) {
      const bridgeApi = initBridge(embedParams.shellOrigin, {
        onContextUpdate: handleContextUpdate,
        onThemeChange: handleThemeChange,
        onNavigate: (path: string) => {
          navigateHandlerRef.current?.(path);
        },
      });
      setBridge(bridgeApi);

      return () => {
        bridgeApi.cleanup();
      };
    }
  }, [embedParams, handleContextUpdate, handleThemeChange]);

  const value: EmbedContextValue = {
    isEmbedded: embedParams.isEmbedded,
    isReady,
    shellContext,
    bridge,
    embedParams,
    theme,
    registerNavigateHandler,
  };

  return (
    <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
  );
}

export function useEmbedContext(): EmbedContextValue {
  const context = useContext(EmbedContext);
  if (!context) {
    throw new Error('useEmbedContext must be used within an EmbedProvider');
  }
  return context;
}
